import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import AuditLogs from './AuditLogs';
import ArchiveStats from './ArchiveStats';
import useConfirm from '../hooks/useConfirm';
import ConfirmModal from './ConfirmModal';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const PDF_EMAIL_LIMIT_BYTES = 15 * 1024 * 1024;

const AdminDashboard = () => {
    // DATA STATES
    const [theses, setTheses] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [requests, setRequests] = useState([]);
    const [requestFilter, setRequestFilter] = useState('pending');

    // DEPARTMENT MANAGEMENT STATES
    const [showDeptModal, setShowDeptModal] = useState(false);
    const [isEditingDept, setIsEditingDept] = useState(false);
    const [editingDeptId, setEditingDeptId] = useState(null);
    const [deptFormData, setDeptFormData] = useState({ name: '', description: '' });
    const [deptDegrees, setDeptDegrees] = useState([]);
    const [newDegreeInput, setNewDegreeInput] = useState('');
    const [newMajorInputs, setNewMajorInputs] = useState({});
    const [resolutionModal, setResolutionModal] = useState(null);
    const [renameModal, setRenameModal] = useState(null);
    const [inlineDegreeEdit, setInlineDegreeEdit] = useState({ index: null, value: '' });
    const [inlineMajorEdit, setInlineMajorEdit] = useState({ degreeIndex: null, majorIndex: null, value: '' });

    // UI & FILTER STATES
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [modalDeptFilter, setModalDeptFilter] = useState('all');
    const [selectedThesisForGeneral, setSelectedThesisForGeneral] = useState(null);
    const [requestUploadFile, setRequestUploadFile] = useState(null);
    const [selectedRequestThesis, setSelectedRequestThesis] = useState(null);
    const [selectedRequestThesisMissing, setSelectedRequestThesisMissing] = useState(false);
    const [requestWarningModal, setRequestWarningModal] = useState(null);
    const { confirm, confirmState, handleClose } = useConfirm();

    // 1. SAFE USER RETRIEVAL
    const userData = localStorage.getItem('user');
    const user = userData ? JSON.parse(userData) : null;

    // 2. FETCHING LOGIC (Wrapped in useCallback to prevent unnecessary re-renders)
    const fetchRequests = useCallback(async () => {
        if (!user?.user_id) return;
        try {
            const statusQuery = requestFilter === 'all' ? '' : `?status=${requestFilter}`;
            const res = await axios.get(`/api/admin/requests${statusQuery}`);
            setRequests(res.data);
        } catch (err) { 
            console.error("Error fetching requests", err); 
        }
    }, [requestFilter, user?.user_id]);

    const fetchArchive = async () => {
        try {
            const res = await axios.get('/api/theses/search');
            setTheses(res.data);
        } catch (err) { console.error("Error fetching archive", err); }
    };

    const fetchDepartments = async () => {
        try {
            const res = await axios.get('/api/departments');
            setDepartments(res.data);
        } catch (err) { console.error("Error fetching departments", err); }
    };

    useEffect(() => {
        fetchArchive();
        fetchDepartments();
        fetchRequests();
    }, [fetchRequests]);

    // 3. DEPARTMENT MANAGEMENT HANDLERS
    const normalizeDegreesForForm = (dept) => {
        if (dept?.degrees && dept.degrees.length > 0) {
            return dept.degrees.map(d => ({
                degree_id: d.degree_id,
                degree_name: d.degree_name,
                majors: (d.majors || []).map(m => ({
                    major_id: m.major_id,
                    major_name: m.major_name,
                    is_default: m.is_default
                }))
            }));
        }
        if (dept?.majors && dept.majors.length > 0) {
            return [{
                degree_name: 'General',
                majors: dept.majors.map(m => ({
                    major_id: m.major_id,
                    major_name: m.major_name,
                    is_default: m.is_default
                }))
            }];
        }
        return [];
    };

    const openAddDeptModal = () => {
        setIsEditingDept(false);
        setEditingDeptId(null);
        setDeptFormData({ name: '', description: '' });
        setDeptDegrees([]);
        setNewDegreeInput('');
        setNewMajorInputs({});
        setInlineDegreeEdit({ index: null, value: '' });
        setInlineMajorEdit({ degreeIndex: null, majorIndex: null, value: '' });
        setShowDeptModal(true);
    };

    const openEditDeptModal = (dept) => {
        setIsEditingDept(true);
        setEditingDeptId(dept.dept_id);
        setDeptFormData({ name: dept.dept_name, description: dept.description || '' });
        setDeptDegrees(normalizeDegreesForForm(dept));
        setNewDegreeInput('');
        setNewMajorInputs({});
        setInlineDegreeEdit({ index: null, value: '' });
        setInlineMajorEdit({ degreeIndex: null, majorIndex: null, value: '' });
        setShowDeptModal(true);
    };

    const closeDeptModal = () => {
        setShowDeptModal(false);
        setIsEditingDept(false);
        setEditingDeptId(null);
        setDeptFormData({ name: '', description: '' });
        setDeptDegrees([]);
        setNewDegreeInput('');
        setNewMajorInputs({});
        setInlineDegreeEdit({ index: null, value: '' });
        setInlineMajorEdit({ degreeIndex: null, majorIndex: null, value: '' });
    };

    const handleAddDegreeToForm = () => {
        if (!newDegreeInput.trim()) return;
        if (deptDegrees.some(d => d.degree_name === newDegreeInput.trim())) {
            alert("This degree already exists");
            return;
        }
        const next = [
            ...deptDegrees,
            { degree_name: newDegreeInput.trim(), majors: [], is_default: deptDegrees.length === 0 }
        ];
        setDeptDegrees(next);
        setNewDegreeInput('');
    };

    const handleRemoveDegreeFromForm = (index) => {
        const next = deptDegrees.filter((_, i) => i !== index);
        if (next.length > 0 && !next.some(d => d.is_default)) {
            next[0].is_default = true;
        }
        setDeptDegrees(next);
        if (inlineDegreeEdit.index === index) {
            setInlineDegreeEdit({ index: null, value: '' });
        }
        if (inlineMajorEdit.degreeIndex === index) {
            setInlineMajorEdit({ degreeIndex: null, majorIndex: null, value: '' });
        }
    };

    const handleAddMajorToDegree = (degreeIndex) => {
        const input = (newMajorInputs[degreeIndex] || '').trim();
        if (!input) return;
        const degree = deptDegrees[degreeIndex];
        if (!degree) return;
        if ((degree.majors || []).some(m => m.major_name === input)) {
            alert("This major already exists in this degree");
            return;
        }
        const updatedDegrees = [...deptDegrees];
        const majors = [...(updatedDegrees[degreeIndex].majors || [])];
        majors.push({ major_name: input, is_default: majors.length === 0 });
        updatedDegrees[degreeIndex] = { ...updatedDegrees[degreeIndex], majors };
        setDeptDegrees(updatedDegrees);
        setNewMajorInputs(prev => ({ ...prev, [degreeIndex]: '' }));
    };

    const handleRemoveMajorFromDegree = (degreeIndex, majorIndex) => {
        const updatedDegrees = [...deptDegrees];
        const majors = (updatedDegrees[degreeIndex]?.majors || []).filter((_, i) => i !== majorIndex);
        if (majors.length > 0 && !majors.some(m => m.is_default)) {
            majors[0].is_default = true;
        }
        updatedDegrees[degreeIndex] = { ...updatedDegrees[degreeIndex], majors };
        setDeptDegrees(updatedDegrees);
        if (inlineMajorEdit.degreeIndex === degreeIndex && inlineMajorEdit.majorIndex === majorIndex) {
            setInlineMajorEdit({ degreeIndex: null, majorIndex: null, value: '' });
        }
    };

    const conflictKey = (conflict) => `${conflict.type}:${conflict.id}`;

    const createResolutionModal = (mode, conflicts, extra = {}) => {
        const selections = {};
        conflicts.forEach(conflict => {
            selections[conflictKey(conflict)] = {
                action: 'delete_theses',
                targetDeptId: '',
                targetDegreeId: '',
                targetMajorId: ''
            };
        });
        setResolutionModal({ mode, conflicts, selections, ...extra });
    };

    const getDeptById = (deptId) => departments.find(d => String(d.dept_id) === String(deptId));

    const getDegreesForResolution = (selection = {}) => {
        const targetDept = getDeptById(selection.targetDeptId);
        return targetDept?.degrees || [];
    };

    const getMajorsForResolution = (selection = {}) => {
        const targetDegree = getDegreesForResolution(selection).find(d => String(d.degree_id) === String(selection.targetDegreeId));
        return targetDegree?.majors || [];
    };

    const updateResolutionSelection = (conflict, patch) => {
        setResolutionModal(prev => {
            if (!prev) return prev;
            const key = conflictKey(conflict);
            const previous = prev.selections[key] || {};
            const nextSelection = { ...previous, ...patch };

            if (Object.prototype.hasOwnProperty.call(patch, 'targetDeptId')) {
                nextSelection.targetDegreeId = '';
                nextSelection.targetMajorId = '';
            }
            if (Object.prototype.hasOwnProperty.call(patch, 'targetDegreeId')) {
                nextSelection.targetMajorId = '';
            }

            return {
                ...prev,
                selections: {
                    ...prev.selections,
                    [key]: nextSelection
                }
            };
        });
    };

    const handleDeleteMajor = async (majorId, majorName) => {
        if (!user?.user_id) return;
        try {
            const ok = await confirm({
                title: 'Delete Major',
                message: `Delete the "${majorName || 'this'}" major?`,
                confirmText: 'Delete',
                cancelText: 'Cancel',
                danger: true
            });
            if (!ok) return;

            await axios.delete(`/api/majors/${majorId}`, { params: { adminId: user.user_id } });
            fetchDepartments();
        } catch (err) {
            if (err.response?.status === 409) {
                createResolutionModal('delete-item', [{
                    type: 'major',
                    id: majorId,
                    name: majorName || 'this major',
                    thesisCount: err.response.data?.thesisCount || 0
                }]);
                return;
            }
            alert("Failed to delete major");
            console.error(err);
        }
    };

    const handleDeleteDegree = async (degreeId, degreeName) => {
        if (!user?.user_id) return;
        try {
            const ok = await confirm({
                title: 'Delete Degree',
                message: `Delete the "${degreeName || 'this'}" degree?`,
                confirmText: 'Delete',
                cancelText: 'Cancel',
                danger: true
            });
            if (!ok) return;

            await axios.delete(`/api/degrees/${degreeId}`, { params: { adminId: user.user_id } });
            fetchDepartments();
        } catch (err) {
            if (err.response?.status === 409) {
                createResolutionModal('delete-item', [{
                    type: 'degree',
                    id: degreeId,
                    name: degreeName || 'this degree',
                    thesisCount: err.response.data?.thesisCount || 0
                }]);
                return;
            }
            alert("Failed to delete degree");
            console.error(err);
        }
    };

    const openRenameModal = (type, id, currentName) => {
        if (!user?.user_id) return;
        setRenameModal({
            type,
            id,
            currentName: currentName || '',
            value: currentName || ''
        });
    };

    const closeRenameModal = () => {
        setRenameModal(null);
    };

    const submitRenameModal = async () => {
        if (!renameModal || !user?.user_id) return;
        const trimmed = String(renameModal.value || '').trim();
        if (!trimmed) {
            alert('Please enter a name.');
            return;
        }
        if (trimmed === renameModal.currentName) {
            closeRenameModal();
            return;
        }
        try {
            if (renameModal.type === 'degree') {
                await axios.put(`/api/degrees/${renameModal.id}`, { degree_name: trimmed, adminId: user.user_id });
            } else {
                await axios.put(`/api/majors/${renameModal.id}`, { major_name: trimmed, adminId: user.user_id });
            }
            await fetchDepartments();
            setDeptDegrees(prev => prev.map(d => {
                if (renameModal.type === 'degree' && d.degree_id === renameModal.id) {
                    return { ...d, degree_name: trimmed };
                }
                if (renameModal.type === 'major') {
                    const majors = (d.majors || []).map(m => m.major_id === renameModal.id ? { ...m, major_name: trimmed } : m);
                    return { ...d, majors };
                }
                return d;
            }));
            closeRenameModal();
        } catch (err) {
            const msg = err.response?.data || `Failed to update ${renameModal.type} name`;
            alert(msg);
            console.error(err);
        }
    };

    const startInlineDegreeEdit = (degreeIndex, currentName) => {
        setInlineDegreeEdit({ index: degreeIndex, value: currentName || '' });
    };

    const cancelInlineDegreeEdit = () => {
        setInlineDegreeEdit({ index: null, value: '' });
    };

    const saveInlineDegreeEdit = async (degreeIndex) => {
        const degree = deptDegrees[degreeIndex];
        if (!degree) return;
        const trimmed = String(inlineDegreeEdit.value || '').trim();
        if (!trimmed) {
            alert('Please enter a degree name.');
            return;
        }
        if (trimmed === degree.degree_name) {
            cancelInlineDegreeEdit();
            return;
        }
        setDeptDegrees(prev => {
            const updated = [...prev];
            if (updated[degreeIndex]) {
                updated[degreeIndex] = { ...updated[degreeIndex], degree_name: trimmed };
            }
            return updated;
        });
        cancelInlineDegreeEdit();
    };

    const startInlineMajorEdit = (degreeIndex, majorIndex, currentName) => {
        setInlineMajorEdit({ degreeIndex, majorIndex, value: currentName || '' });
    };

    const cancelInlineMajorEdit = () => {
        setInlineMajorEdit({ degreeIndex: null, majorIndex: null, value: '' });
    };

    const saveInlineMajorEdit = async (degreeIndex, majorIndex) => {
        const degree = deptDegrees[degreeIndex];
        const major = degree?.majors?.[majorIndex];
        if (!major) return;
        const trimmed = String(inlineMajorEdit.value || '').trim();
        if (!trimmed) {
            alert('Please enter a major name.');
            return;
        }
        if (trimmed === major.major_name) {
            cancelInlineMajorEdit();
            return;
        }
        setDeptDegrees(prev => {
            const updated = [...prev];
            const targetDegree = updated[degreeIndex];
            if (targetDegree?.majors?.[majorIndex]) {
                const majors = [...targetDegree.majors];
                majors[majorIndex] = { ...majors[majorIndex], major_name: trimmed };
                updated[degreeIndex] = { ...targetDegree, majors };
            }
            return updated;
        });
        cancelInlineMajorEdit();
    };

    const buildDepartmentPayload = (resolutions = []) => ({
        dept_name: deptFormData.name.trim(),
        description: deptFormData.description.trim() || null,
        degrees: deptDegrees.map(d => ({
            degree_id: d.degree_id,
            degree_name: d.degree_name,
            majors: (d.majors || []).map(m => ({
                major_id: m.major_id,
                major_name: m.major_name
            }))
        })),
        resolutions,
        adminId: user.user_id
    });

    const buildResolutionPayloads = () => {
        if (!resolutionModal) return [];
        return resolutionModal.conflicts.map(conflict => {
            const selection = resolutionModal.selections[conflictKey(conflict)] || {};
            return {
                type: conflict.type,
                id: conflict.id,
                action: selection.action,
                targetDeptId: selection.targetDeptId || undefined,
                targetDegreeId: selection.targetDegreeId || undefined,
                targetMajorId: selection.targetMajorId || undefined
            };
        });
    };

    const validateResolutionPayloads = (payloads) => {
        for (const item of payloads) {
            if (item.action === 'move_to') {
                if (item.type === 'department' && !item.targetDeptId) {
                    alert('Please choose a target department.');
                    return false;
                }
                if (item.type === 'degree' && !item.targetDegreeId) {
                    alert('Please choose a target degree or major.');
                    return false;
                }
                if (item.type === 'major' && !item.targetMajorId) {
                    alert('Please choose a target major.');
                    return false;
                }
            }
        }
        return true;
    };

    const submitDeleteResolution = async () => {
        if (!resolutionModal || !user?.user_id) return;
        const payloads = buildResolutionPayloads();
        if (!validateResolutionPayloads(payloads)) return;
        try {
            for (const payload of payloads) {
                if (payload.type === 'department') {
                    await axios.delete(`/api/departments/${payload.id}`, {
                        params: { adminId: user.user_id, ...payload }
                    });
                } else {
                    const url = payload.type === 'degree' ? `/api/degrees/${payload.id}` : `/api/majors/${payload.id}`;
                    await axios.delete(url, { params: { adminId: user.user_id, ...payload } });
                }
            }
            setResolutionModal(null);
            await fetchDepartments();
            await fetchArchive();
        } catch (err) {
            alert("Failed to complete delete action");
            console.error(err);
        }
    };

    const handleSaveDepartment = async (resolutions = []) => {
        if (!deptFormData.name.trim()) {
            alert("Please enter a department name");
            return;
        }
        if (!user?.user_id) return;

        try {
            const oldDept = departments.find(d => d.dept_id === editingDeptId);
            const oldDegrees = oldDept?.degrees || [];
            const oldDegreesByName = new Map(oldDegrees.map(d => [d.degree_name, d]));
            const oldDegreesById = new Map(oldDegrees.map(d => [d.degree_id, d]));

            const isAddingFirstDegree = isEditingDept && oldDegrees.length === 0 && deptDegrees.length > 0;
            const degreesNeedingMajorAssign = new Map();

            deptDegrees.forEach(d => {
                const old = d.degree_id ? oldDegreesById.get(d.degree_id) : oldDegreesByName.get(d.degree_name);
                const oldMajorCount = old?.majors?.length || 0;
                const newMajorCount = d.majors?.length || 0;
                if (old && oldMajorCount === 0 && newMajorCount > 0) {
                    const key = d.degree_id ? `id:${d.degree_id}` : `name:${d.degree_name}`;
                    degreesNeedingMajorAssign.set(key, { degree_id: d.degree_id || null, degree_name: d.degree_name });
                }
            });

            if (isAddingFirstDegree && deptDegrees[0]?.majors?.length > 0) {
                const d = deptDegrees[0];
                const key = d.degree_id ? `id:${d.degree_id}` : `name:${d.degree_name}`;
                degreesNeedingMajorAssign.set(key, { degree_id: d.degree_id || null, degree_name: d.degree_name });
            }

            let confirmMessage = '';
            if (isEditingDept) {
                confirmMessage = `Update the "${deptFormData.name.trim()}" department?`;
                if (isAddingFirstDegree && deptDegrees.length > 0) {
                    confirmMessage += `\n\nNote: This department previously had no degrees. All existing thesis projects under this department will be automatically assigned to the first degree "${deptDegrees[0].degree_name}".`;
                }
                if (degreesNeedingMajorAssign.size > 0) {
                    const lines = Array.from(degreesNeedingMajorAssign.values()).map(item => {
                        const degree = deptDegrees.find(d => (item.degree_id ? d.degree_id === item.degree_id : d.degree_name === item.degree_name));
                        const firstMajor = degree?.majors?.[0]?.major_name;
                        if (!firstMajor) return null;
                        return `Note: Degree "${item.degree_name}" previously had no majors. All existing thesis projects under this degree will be automatically assigned to the first major "${firstMajor}".`;
                    }).filter(Boolean);
                    if (lines.length > 0) {
                        confirmMessage += `\n\n${lines.join('\n')}`;
                    }
                }
            } else {
                confirmMessage = `Add new department "${deptFormData.name.trim()}"?`;
                if (deptDegrees.length > 0) {
                    confirmMessage += `\n\nDegrees: ${deptDegrees.map(d => d.degree_name).join(', ')}`;
                }
            }

            const ok = await confirm({
                title: isEditingDept ? 'Update Department' : 'Add Department',
                message: confirmMessage,
                confirmText: isEditingDept ? 'Update' : 'Add',
                cancelText: 'Cancel'
            });
            if (!ok) return;

            const payload = buildDepartmentPayload(resolutions);

            if (isEditingDept) {
                await axios.put(`/api/departments/${editingDeptId}`, payload);
            } else {
                await axios.post('/api/departments', payload);
            }

            const updatedDepts = await axios.get('/api/departments');
            setDepartments(updatedDepts.data);

            const updatedDept = updatedDepts.data.find(d => d.dept_id === editingDeptId);

            if (isAddingFirstDegree && updatedDept?.degrees?.length) {
                const firstDegreeName = deptDegrees[0]?.degree_name;
                const firstDegree = updatedDept.degrees.find(d => d.degree_name === firstDegreeName) || updatedDept.degrees[0];
                if (firstDegree?.degree_id) {
                    const assignRes = await axios.post(
                        `/api/degrees/${firstDegree.degree_id}/assign-theses`,
                        { adminId: user.user_id }
                    );
                    alert(`OK: ${assignRes.data.message}`);
                }
            }

            if (degreesNeedingMajorAssign.size > 0 && updatedDept?.degrees?.length) {
                for (const item of degreesNeedingMajorAssign.values()) {
                    const degree = item.degree_id
                        ? updatedDept.degrees.find(d => d.degree_id === item.degree_id)
                        : updatedDept.degrees.find(d => d.degree_name === item.degree_name);
                    const firstMajorId = degree?.majors?.[0]?.major_id;
                    if (!firstMajorId) continue;
                    const assignRes = await axios.post(
                        `/api/majors/${firstMajorId}/assign-theses`,
                        { adminId: user.user_id }
                    );
                    alert(`OK: ${assignRes.data.message}`);
                }
            }

            closeDeptModal();
        } catch (err) {
            if (err.response?.status === 409 && err.response.data?.error === 'DEPARTMENT_SAVE_NEEDS_RESOLUTION') {
                createResolutionModal('save-department', err.response.data.conflicts || []);
                return;
            }
            alert("Failed to save department");
            console.error(err);
        }
    };

    const submitDepartmentSaveResolution = async () => {
        const payloads = buildResolutionPayloads();
        if (!validateResolutionPayloads(payloads)) return;
        setResolutionModal(null);
        await handleSaveDepartment(payloads);
    };

    const handleDeleteDepartment = async (id, name) => {
        if (!user?.user_id) return;
        const ok = await confirm({
            title: 'Delete Department',
            message: `Delete the "${name}" department?`,
            confirmText: 'Delete',
            cancelText: 'Cancel',
            danger: true
        });
        if (!ok) return;
        try {
            await axios.delete(`/api/departments/${id}?adminId=${user.user_id}`);
            await fetchDepartments();
            await fetchArchive();
        } catch (err) {
            if (err.response?.status === 409) {
                createResolutionModal('delete-item', [{
                    type: 'department',
                    id,
                    name,
                    thesisCount: err.response.data?.thesisCount || 0
                }]);
                return;
            }
            alert("Failed to delete. Ensure no theses are linked to it.");
        }
    };

    const handleRequestAction = async (req, action, manualThesis = null) => {
        if (!user?.user_id) return;
        let thesis = manualThesis || selectedRequestThesis || null;
        let filePath = manualThesis ? manualThesis.file_path : (req.file_path || req.filePath || thesis?.file_path || '');
        const thesisTitle = thesis?.title || req.thesis_title;

        if (action === 'approve') {
            if (req.thesis_id && !manualThesis && selectedRequestThesisMissing) {
                openMissingThesisModal(req);
                return;
            }

            if (thesis && !filePath && !requestUploadFile) {
                alert('Please upload a PDF file to approve this request.');
                return;
            }

            const limitCheck = await validateRequestPdfLimit({
                request: req,
                thesis,
                filePath,
                uploadFile: requestUploadFile
            });
            if (limitCheck.blocked) {
                return;
            }
        }

        const ok = await confirm({
            title: 'Confirm Request',
            message: `Are you sure you want to ${action} this request?`,
            confirmText: action === 'approve' ? 'Approve' : 'Reject',
            cancelText: 'Cancel'
        });
        if (!ok) return;

        setIsProcessing(true);
        try {
            if (action === 'approve' && thesis && !filePath) {
                const data = new FormData();
                data.append('thesis_id', thesis.thesis_id);
                data.append('title', thesis.title || '');
                data.append('year', thesis.year || '');
                data.append('department', thesis.department || '');
                data.append('abstract', thesis.abstract || '');
                if (thesis.degree_id) {
                    data.append('degreeId', thesis.degree_id);
                }
                if (thesis.major_id) {
                    data.append('majorId', thesis.major_id);
                }
                data.append('thesisFile', requestUploadFile);
                data.append('userId', user.user_id);

                await axios.put(`/api/theses/${thesis.thesis_id}`, data);
                const refreshed = await axios.get(`/api/theses/search?thesis_id=${thesis.thesis_id}`);
                thesis = refreshed.data[0];
                filePath = thesis?.file_path || '';
            }

            await axios.post('/api/admin/requests/respond', {
                request_id: req.request_id,
                email: req.requester_email,
                thesis_title: thesisTitle || "General Inquiry",
                filePath: filePath || null,
                adminId: user.user_id,
                action: action
            });

            alert(`Request ${action === 'approve' ? 'Approved' : 'Rejected'} successfully.`);
            closeModal();
            fetchRequests();
        } catch (err) {
            alert(err.response?.data?.error || "Action failed.");
            console.error(err);
        } finally {
            setIsProcessing(false);
        }
    };

    const performDeleteRequest = async (req) => {
        setIsProcessing(true);
        try {
            await axios.delete(`/api/admin/requests/${req.request_id}?adminId=${user.user_id}`);
            alert("Request deleted successfully.");
            setRequestWarningModal(null);
            closeModal();
            fetchRequests();
        } catch (err) {
            alert("Failed to delete request.");
            console.error(err);
        } finally {
            setIsProcessing(false);
        }
    };

    const handleDeleteRequest = async (req) => {
        if (!user?.user_id) return;
        const ok = await confirm({
            title: 'Delete Request',
            message: 'Delete this request permanently? This action cannot be undone.',
            confirmText: 'Delete',
            cancelText: 'Cancel',
            danger: true
        });
        if (!ok) return;
        await performDeleteRequest(req);
    };

    const formatFileSize = (sizeBytes) => {
        if (!Number.isFinite(sizeBytes) || sizeBytes <= 0) return '';
        const mb = sizeBytes / (1024 * 1024);
        return `${mb.toFixed(mb >= 10 ? 1 : 2)} MB`;
    };

    const openRequestWarningModal = (modal) => {
        setRequestWarningModal(modal);
    };

    const openPdfLimitModal = ({ request, thesis, sizeBytes, source }) => {
        openRequestWarningModal({
            request,
            kind: 'file_too_large',
            title: 'PDF Too Large To Email',
            thesisTitle: thesis?.title || request?.thesis_title || 'this thesis',
            requesterEmail: request?.requester_email || '',
            sizeBytes,
            source
        });
    };

    const openMissingThesisModal = (request) => {
        openRequestWarningModal({
            request,
            kind: 'missing_thesis',
            title: 'Thesis Could Not Be Found',
            thesisTitle: request?.thesis_title || 'this thesis',
            requesterEmail: request?.requester_email || ''
        });
    };

    const validateRequestPdfLimit = async ({ request, thesis, filePath, uploadFile }) => {
        if (uploadFile) {
            if (uploadFile.type && uploadFile.type !== 'application/pdf') {
                alert('Only PDF files can be uploaded.');
                return { blocked: true };
            }
            if (uploadFile.size > PDF_EMAIL_LIMIT_BYTES) {
                openPdfLimitModal({
                    request,
                    thesis,
                    sizeBytes: uploadFile.size,
                    source: 'upload'
                });
                return { blocked: true };
            }
            return { blocked: false };
        }

        if (!filePath || !thesis) {
            return { blocked: false };
        }

        try {
            const res = await axios.get('/api/files/metadata', {
                params: { path: filePath, userId: user.user_id }
            });
            const sizeBytes = Number(res.data?.sizeBytes || 0);
            if (sizeBytes > PDF_EMAIL_LIMIT_BYTES) {
                openPdfLimitModal({
                    request,
                    thesis,
                    sizeBytes,
                    source: 'existing'
                });
                return { blocked: true };
            }
        } catch (err) {
            console.error('Unable to inspect PDF size for request email:', err);
        }

        return { blocked: false };
    };

    const handleRequestUploadChange = (req, thesis, file) => {
        if (!file) {
            setRequestUploadFile(null);
            return;
        }
        if (file.type && file.type !== 'application/pdf') {
            alert('Only PDF files can be uploaded.');
            setRequestUploadFile(null);
            return;
        }
        if (file.size > PDF_EMAIL_LIMIT_BYTES) {
            setRequestUploadFile(null);
            openPdfLimitModal({
                request: req,
                thesis,
                sizeBytes: file.size,
                source: 'upload'
            });
            return;
        }
        setRequestUploadFile(file);
    };

    const closeModal = () => {
        setSelectedRequest(null);
        setSelectedThesisForGeneral(null);
        setSearchTerm('');
        setModalDeptFilter('all');
        setRequestUploadFile(null);
        setSelectedRequestThesis(null);
        setSelectedRequestThesisMissing(false);
        setRequestWarningModal(null);
    };
    
    useEffect(() => {
        let active = true;
        const loadRequestThesis = async () => {
            if (!selectedRequest?.thesis_id) {
                setSelectedRequestThesis(null);
                setSelectedRequestThesisMissing(false);
                setRequestUploadFile(null);
                return;
            }
            try {
                const res = await axios.get(`/api/theses/search?thesis_id=${selectedRequest.thesis_id}`);
                if (active) {
                    setSelectedRequestThesis(res.data[0] || null);
                    setSelectedRequestThesisMissing((res.data?.length || 0) === 0);
                    setRequestUploadFile(null);
                }
            } catch {
                if (active) {
                    setSelectedRequestThesis(null);
                    setSelectedRequestThesisMissing(true);
                    setRequestUploadFile(null);
                }
            }
        };
        loadRequestThesis();
        return () => { active = false; };
    }, [selectedRequest?.thesis_id]);
    // 4. FILTERING LOGIC
    const filteredTheses = theses.filter(t => {
        // Use optional chaining and default to empty string to prevent .toLowerCase() errors
        const title = t.title || "";
        const matchesSearch = title.toLowerCase().includes(searchTerm.toLowerCase());

        // Department matching: support theses that store either `dept_id` or `department` (name)
        let matchesDept = false;
        if (modalDeptFilter === 'all') matchesDept = true;
        else {
            // Direct match against a numeric dept_id if present
            if (t.dept_id && String(t.dept_id) === String(modalDeptFilter)) matchesDept = true;

            // Direct match against a department name stored on the thesis
            if (!matchesDept && t.department && String(t.department) === String(modalDeptFilter)) matchesDept = true;

            // If modal filter is an id but thesis stores only department name, resolve the name and compare
            if (!matchesDept) {
                const deptObj = departments.find(d => String(d.dept_id) === String(modalDeptFilter));
                if (deptObj && t.department && String(t.department) === String(deptObj.dept_name)) matchesDept = true;
            }
        }

        return matchesSearch && matchesDept;
    });

    const getStatusClass = (status) => {
        switch(status) {
            case 'completed': return 'badge badge-success';
            case 'rejected': return 'badge badge-danger';
            default: return 'badge badge-warning';
        }
    };

    // RENDER
    return (
        <div className="page">
            
            {isProcessing && (
                <div className="loading-overlay">
                    <div className="loading-card"><h3>Processing...</h3></div>
                </div>
            )}

            <div className="toolbar">
                <h2 className="title">Administrative Dashboard</h2>
                <div className="row">
                    <button onClick={() => window.open(`${API_BASE}/api/export`, '_blank')} className="btn btn-success">Export Excel</button>
                </div>
            </div>

            <div className="section">
                <div className="section-header">
                    <h3 style={{ margin: 0 }}>📩 PDF Requests</h3>
                    <div className="row">
                        <span style={{ fontSize: '0.9rem', color: '#fff' }}>Filter Status:</span>
                        <select 
                            value={requestFilter} 
                            onChange={(e) => setRequestFilter(e.target.value)} 
                            className="select"
                        >
                            <option value="all">All Requests</option>
                            <option value="pending">Pending</option>
                            <option value="completed">Completed</option>
                            <option value="rejected">Rejected</option>
                        </select>
                    </div>
                </div>

                <div className="section-body" style={{ maxHeight: '450px', overflowY: 'auto' }}>
                    <table className="table">
                        <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                            <tr>
                                <th>Requester</th>
                                <th>Item Requested</th>
                                <th>Status</th>
                                <th style={{ textAlign: 'right' }}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {requests.length === 0 ? (
                                <tr><td colSpan="4" style={{ padding: '40px', textAlign: 'center' }} className="text-muted">No requests found.</td></tr>
                            ) : (
                                requests.map(r => (
                                    <tr key={r.request_id} onClick={() => setSelectedRequest(r)} className="table-row">
                                        <td>
                                            <div style={{ fontWeight: 'bold' }}>{r.requester_name}</div>
                                            <div style={{ fontSize: '0.8rem', color: '#666' }}>{r.requester_email}</div>
                                        </td>
                                        <td>
                                            <div style={{ fontWeight: '500', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {r.thesis_title || "General / Not Specified"}
                                            </div>
                                        </td>
                                        <td>
                                            <span className={getStatusClass(r.status)}>{r.status.toUpperCase()}</span>
                                        </td>
                                        <td style={{ textAlign: 'right', fontWeight: 'bold' }} className="text-maroon">
                                            {r.status === 'pending' ? 'Review →' : 'View Details'}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {selectedRequest && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal" style={{ maxWidth: '720px', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }} onClick={(e) => e.stopPropagation()}>
                        <h3 style={{ marginTop: 0 }}>
                            {selectedRequest.status === 'pending' ? 'Review Request' : 'Request Details'}
                        </h3>

                        <div className="stack" style={{ gap: '14px', overflowY: 'auto', paddingRight: '8px', flex: 1 }}>
                            <div className="card" style={{ padding: '14px' }}>
                                <div style={{ fontWeight: 'bold', marginBottom: '6px' }}>{selectedRequest.requester_name}</div>
                                <div className="text-muted" style={{ fontSize: '0.9rem', marginBottom: '6px' }}>
                                    {selectedRequest.requester_email}
                                </div>
                                <div style={{ fontSize: '0.9rem', marginBottom: '6px' }}>
                                    <strong>Status:</strong> <span className={getStatusClass(selectedRequest.status)}>{selectedRequest.status.toUpperCase()}</span>
                                </div>
                                <div style={{ fontSize: '0.9rem', marginBottom: '6px' }}>
                                    <strong>Requested Item:</strong> {selectedRequest.thesis_title || 'General / Not Specified'}
                                </div>
                                <div style={{ fontSize: '0.9rem' }}>
                                    <strong>Submitted:</strong> {selectedRequest.created_at ? new Date(selectedRequest.created_at).toLocaleString() : 'N/A'}
                                </div>
                            </div>

                            <div>
                                <h4 style={{ margin: '0 0 8px 0' }}>Message</h4>
                                <div className="card" style={{ padding: '14px', whiteSpace: 'pre-wrap', minHeight: '90px' }}>
                                    {selectedRequest.message || 'No message provided.'}
                                </div>
                            </div>

                            {(selectedRequest.request_type === 'general' || !selectedRequest.thesis_id) && (
                                <div>
                                    <h4 style={{ margin: '0 0 8px 0' }}>Optional Thesis Match</h4>
                                    <div className="row" style={{ marginBottom: '10px', flexWrap: 'wrap' }}>
                                        <input
                                            type="text"
                                            className="input"
                                            placeholder="Search thesis title..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            style={{ flex: 2, minWidth: '220px' }}
                                        />
                                        <select
                                            className="input"
                                            value={modalDeptFilter}
                                            onChange={(e) => setModalDeptFilter(e.target.value)}
                                            style={{ flex: 1, minWidth: '180px' }}
                                        >
                                            <option value="all">All Departments</option>
                                            {departments.map((dept) => (
                                                <option key={dept.dept_id} value={dept.dept_id}>{dept.dept_name}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <select
                                        className="input"
                                        value={selectedThesisForGeneral?.thesis_id || ''}
                                        onChange={(e) => {
                                            const thesis = filteredTheses.find((t) => String(t.thesis_id) === String(e.target.value)) || null;
                                            setSelectedThesisForGeneral(thesis);
                                            setRequestUploadFile(null);
                                        }}
                                    >
                                        <option value="">No thesis selected</option>
                                        {filteredTheses.map((thesis) => (
                                            <option key={thesis.thesis_id} value={thesis.thesis_id}>
                                                {thesis.title}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {(selectedRequestThesis || selectedThesisForGeneral) && (
                                <div>
                                    <h4 style={{ margin: '0 0 8px 0' }}>Selected Thesis</h4>
                                    <div className="card" style={{ padding: '14px' }}>
                                        <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                                            {(selectedRequestThesis || selectedThesisForGeneral)?.title}
                                        </div>
                                        <div className="text-muted" style={{ fontSize: '0.9rem' }}>
                                            {[(selectedRequestThesis || selectedThesisForGeneral)?.department, (selectedRequestThesis || selectedThesisForGeneral)?.year].filter(Boolean).join(' • ')}
                                        </div>
                                        {selectedRequest.status === 'pending' && !(selectedRequestThesis || selectedThesisForGeneral)?.file_path && (
                                            <div style={{ marginTop: '10px' }}>
                                                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', fontSize: '0.9rem' }}>
                                                    Upload PDF For Approval
                                                </label>
                                                <input
                                                    type="file"
                                                    accept="application/pdf"
                                                    className="input"
                                                    onChange={(e) => handleRequestUploadChange(selectedRequest, selectedRequestThesis || selectedThesisForGeneral, e.target.files?.[0] || null)}
                                                />
                                                <div className="text-muted" style={{ fontSize: '0.8rem', marginTop: '6px' }}>
                                                    PDFs attached here must stay within 15MB so they can be sent by email.
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {selectedRequest?.thesis_id && selectedRequestThesisMissing && !selectedThesisForGeneral && (
                                <div>
                                    <h4 style={{ margin: '0 0 8px 0' }}>Selected Thesis</h4>
                                    <div className="card" style={{ padding: '14px', border: '1px solid #f1c40f', background: '#fffbea' }}>
                                        <div style={{ fontWeight: 'bold', marginBottom: '4px', color: '#7a5d00' }}>
                                            Thesis could not be found
                                        </div>
                                        <div className="text-muted" style={{ fontSize: '0.9rem' }}>
                                            This request is linked to a thesis record that appears to have been deleted.
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="row" style={{ marginTop: '18px', flexWrap: 'wrap', paddingTop: '12px', borderTop: '1px solid #eee' }}>
                            {selectedRequest.status === 'pending' && (
                                <>
                                    <button
                                        onClick={() => handleRequestAction(selectedRequest, 'approve', selectedThesisForGeneral)}
                                        className="btn btn-success"
                                        style={{ flex: 1 }}
                                    >
                                        Approve
                                    </button>
                                    <button
                                        onClick={() => handleRequestAction(selectedRequest, 'reject', selectedThesisForGeneral)}
                                        className="btn btn-danger"
                                        style={{ flex: 1 }}
                                    >
                                        Reject
                                    </button>
                                </>
                            )}
                            <button
                                onClick={() => handleDeleteRequest(selectedRequest)}
                                className="btn btn-outline"
                                style={{ flex: 1 }}
                            >
                                Delete
                            </button>
                            <button
                                onClick={closeModal}
                                className="btn btn-primary"
                                style={{ flex: 1 }}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {requestWarningModal && (
                <div className="modal-overlay" onClick={() => setRequestWarningModal(null)}>
                    <div className="modal" style={{ maxWidth: '560px' }} onClick={(e) => e.stopPropagation()}>
                        <h3 style={{ marginTop: 0, marginBottom: '12px' }}>{requestWarningModal.title}</h3>
                        {requestWarningModal.kind === 'missing_thesis' ? (
                            <>
                                <p style={{ marginTop: 0, lineHeight: 1.6 }}>
                                    The thesis linked to this request, <strong>{requestWarningModal.thesisTitle}</strong>, could not be found in the archive.
                                </p>
                                <p style={{ lineHeight: 1.6, marginBottom: '18px' }}>
                                    It was likely deleted, so this request can no longer be fulfilled through the system. You can delete the request immediately below.
                                </p>
                            </>
                        ) : (
                            <>
                                <p style={{ marginTop: 0, lineHeight: 1.6 }}>
                                    The PDF for <strong>{requestWarningModal.thesisTitle}</strong> is
                                    {requestWarningModal.sizeBytes ? ` ${formatFileSize(requestWarningModal.sizeBytes)}` : ' over 15MB'},
                                    which is above Brevo&apos;s 15MB attachment limit.
                                </p>
                                <p style={{ lineHeight: 1.6, marginBottom: '18px' }}>
                                    Please send the PDF directly to <strong>{requestWarningModal.requesterEmail || 'the requester'}</strong> instead of approving this request through the system email attachment flow.
                                </p>
                            </>
                        )}
                        <div className="row" style={{ flexWrap: 'wrap' }}>
                            <button
                                onClick={() => performDeleteRequest(requestWarningModal.request)}
                                className="btn btn-danger"
                                style={{ flex: 1 }}
                                disabled={isProcessing}
                            >
                                {isProcessing ? 'Deleting...' : 'Delete Request'}
                            </button>
                            <button
                                onClick={() => setRequestWarningModal(null)}
                                className="btn btn-outline"
                                style={{ flex: 1 }}
                                disabled={isProcessing}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div style={{ marginTop: '30px', marginBottom: '30px' }}><ArchiveStats /></div>
            <AuditLogs />

            <div className="section" style={{ marginTop: '30px' }}>
                <div className="section-header dark">
                    <h3 style={{ margin: 0 }}>🏢 Manage Departments</h3>
                    <button onClick={openAddDeptModal} className="btn btn-primary">+ Add Department</button>
                </div>
                <div className="section-body">
                    <div className="grid grid-auto">
                        {departments.map(dept => (
                            <div key={dept.dept_id} className="card card-hover" style={{ background: '#fafafa' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '10px' }}>
                                    <h4 style={{ margin: '0 0 5px 0', color: '#800000' }}>{dept.dept_name}</h4>
                                    <div className="row">
                                        <button onClick={() => openEditDeptModal(dept)} className="btn btn-outline btn-small">Edit</button>
                                        <button onClick={() => handleDeleteDepartment(dept.dept_id, dept.dept_name)} className="btn btn-danger btn-small">Delete</button>
                                    </div>
                                </div>
                                {dept.description && <p style={{ margin: '5px 0 10px 0', fontSize: '0.9rem', color: '#555', fontStyle: 'italic' }}>{dept.description}</p>}
                                {dept.degrees && dept.degrees.length > 0 ? (
                                    <div className="card" style={{ padding: '12px' }}>
                                        <p style={{ margin: '0 0 8px 0', fontSize: '0.85rem', fontWeight: 'bold', color: '#333' }}>Degrees:</p>
                                        {dept.degrees.map(degree => (
                                            <div key={degree.degree_id} style={{ borderBottom: '1px solid #f0f0f0', padding: '8px 0' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.9rem', marginBottom: '6px' }}>
                                                    <strong>{degree.degree_name}</strong>
                                                    <div style={{ display: 'flex', gap: '6px' }}>
                                                        <button onClick={() => openRenameModal('degree', degree.degree_id, degree.degree_name)} className="btn btn-ghost btn-small">Edit</button>
                                                        <button onClick={() => handleDeleteDegree(degree.degree_id, degree.degree_name)} className="btn btn-ghost btn-small text-danger">Delete</button>
                                                    </div>
                                                </div>
                                                {degree.majors && degree.majors.length > 0 ? (
                                                    <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: '4px', padding: '6px 10px' }}>
                                                        {degree.majors.map(major => (
                                                            <div key={major.major_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', fontSize: '0.85rem', borderBottom: '1px solid #f7f7f7' }}>
                                                                <span>{major.major_name}</span>
                                                                <div style={{ display: 'flex', gap: '6px' }}>
                                                                    <button onClick={() => openRenameModal('major', major.major_id, major.major_name)} className="btn btn-ghost btn-small">Edit</button>
                                                                    <button onClick={() => handleDeleteMajor(major.major_id, major.major_name)} className="btn btn-ghost btn-small text-danger">Delete</button>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <p className="text-muted" style={{ margin: 0, fontSize: '0.85rem' }}>No majors added</p>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-muted" style={{ margin: 0, fontSize: '0.85rem' }}>No degrees added</p>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {showDeptModal && (
                <div className="modal-overlay">
                    <div className="modal" style={{ maxWidth: '600px' }}>
                        <h3 style={{ marginTop: 0, marginBottom: '20px' }}>{isEditingDept ? 'Edit Department' : 'Add New Department'}</h3>
                        
                        <div style={{ overflowY: 'auto', flex: 1, paddingRight: '10px' }}>
                            <div style={{ marginBottom: '15px' }}>
                                <label style={{ fontSize: '0.9rem', fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>Department Name</label>
                                <input 
                                    type="text" 
                                    value={deptFormData.name} 
                                    onChange={(e) => setDeptFormData({ ...deptFormData, name: e.target.value })}
                                    placeholder="e.g., Computer Science"
                                    className="input"
                                />
                            </div>

                            <div style={{ marginBottom: '15px' }}>
                                <label style={{ fontSize: '0.9rem', fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>Description (Optional)</label>
                                <textarea 
                                    value={deptFormData.description} 
                                    onChange={(e) => setDeptFormData({ ...deptFormData, description: e.target.value })}
                                    placeholder="e.g., Department focused on computer science and technology"
                                    className="textarea"
                                    style={{ height: '100px' }}
                                />
                            </div>

                            <div style={{ marginBottom: '15px' }}>
                                <label style={{ fontSize: '0.9rem', fontWeight: 'bold', display: 'block', marginBottom: '10px' }}>Degrees (Optional)</label>
                                <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                                    <input
                                        type="text"
                                        value={newDegreeInput}
                                        onChange={(e) => setNewDegreeInput(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddDegreeToForm(); } }}
                                        placeholder="Add a degree (e.g., Bachelor of Secondary Education)"
                                        className="input"
                                        style={{ flex: 1 }}
                                    />
                                    <button onClick={handleAddDegreeToForm} className="btn btn-success btn-small">Add</button>
                                </div>

                                {deptDegrees.length > 0 && (
                                    <div style={{ background: '#f9f9f9', padding: '10px', borderRadius: '4px', border: '1px solid #e0e0e0', maxHeight: '320px', overflowY: 'auto' }}>
                                        {deptDegrees.map((degree, degreeIdx) => (
                                            <div key={degreeIdx} style={{ padding: '10px 8px', borderBottom: degreeIdx < deptDegrees.length - 1 ? '1px solid #eee' : 'none' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                                                    {inlineDegreeEdit.index === degreeIdx ? (
                                                        <input
                                                            type="text"
                                                            value={inlineDegreeEdit.value}
                                                            onChange={(e) => setInlineDegreeEdit({ index: degreeIdx, value: e.target.value })}
                                                            className="input"
                                                            style={{ flex: 1, marginRight: '8px' }}
                                                        />
                                                    ) : (
                                                        <span style={{ fontSize: '0.95rem', fontWeight: 'bold' }}>{degree.degree_name}</span>
                                                    )}
                                                    <div style={{ display: 'flex', gap: '8px' }}>
                                                        {inlineDegreeEdit.index === degreeIdx ? (
                                                            <>
                                                                <button
                                                                    onClick={() => saveInlineDegreeEdit(degreeIdx)}
                                                                    style={{ background: 'transparent', border: 'none', color: '#16a34a', cursor: 'pointer' }}
                                                                >
                                                                    Save
                                                                </button>
                                                                <button
                                                                    onClick={cancelInlineDegreeEdit}
                                                                    style={{ background: 'transparent', border: 'none', color: '#64748b', cursor: 'pointer' }}
                                                                >
                                                                    Cancel
                                                                </button>
                                                            </>
                                                        ) : (
                                                            <button
                                                                onClick={() => startInlineDegreeEdit(degreeIdx, degree.degree_name)}
                                                                style={{ background: 'transparent', border: 'none', color: '#1d4ed8', cursor: 'pointer' }}
                                                            >
                                                                Edit
                                                            </button>
                                                        )}
                                                        <button
                                                            onClick={() => handleRemoveDegreeFromForm(degreeIdx)}
                                                            style={{ background: 'transparent', border: 'none', color: '#dc3545', cursor: 'pointer' }}
                                                        >
                                                            Remove
                                                        </button>
                                                    </div>
                                                </div>
                                                <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                                                    <input
                                                        type="text"
                                                        value={newMajorInputs[degreeIdx] || ''}
                                                        onChange={(e) => setNewMajorInputs(prev => ({ ...prev, [degreeIdx]: e.target.value }))}
                                                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddMajorToDegree(degreeIdx); } }}
                                                        placeholder="Add a major for this degree"
                                                        className="input"
                                                        style={{ flex: 1 }}
                                                    />
                                                    <button onClick={() => handleAddMajorToDegree(degreeIdx)} className="btn btn-success btn-small">Add</button>
                                                </div>
                                                {degree.majors && degree.majors.length > 0 && (
                                                    <div style={{ background: '#fff', borderRadius: '4px', border: '1px solid #eee', padding: '6px 10px' }}>
                                                        {degree.majors.map((major, majorIdx) => (
                                                            <div key={majorIdx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: majorIdx < degree.majors.length - 1 ? '1px solid #f2f2f2' : 'none' }}>
                                                                {inlineMajorEdit.degreeIndex === degreeIdx && inlineMajorEdit.majorIndex === majorIdx ? (
                                                                    <input
                                                                        type="text"
                                                                        value={inlineMajorEdit.value}
                                                                        onChange={(e) => setInlineMajorEdit({ degreeIndex: degreeIdx, majorIndex: majorIdx, value: e.target.value })}
                                                                        className="input"
                                                                        style={{ flex: 1, marginRight: '8px' }}
                                                                    />
                                                                ) : (
                                                                    <span style={{ fontSize: '0.9rem' }}>{major.major_name}</span>
                                                                )}
                                                                <div style={{ display: 'flex', gap: '8px' }}>
                                                                    {inlineMajorEdit.degreeIndex === degreeIdx && inlineMajorEdit.majorIndex === majorIdx ? (
                                                                        <>
                                                                            <button
                                                                                onClick={() => saveInlineMajorEdit(degreeIdx, majorIdx)}
                                                                                style={{ background: 'transparent', border: 'none', color: '#16a34a', cursor: 'pointer' }}
                                                                            >
                                                                                Save
                                                                            </button>
                                                                            <button
                                                                                onClick={cancelInlineMajorEdit}
                                                                                style={{ background: 'transparent', border: 'none', color: '#64748b', cursor: 'pointer' }}
                                                                            >
                                                                                Cancel
                                                                            </button>
                                                                        </>
                                                                    ) : (
                                                                        <button
                                                                            onClick={() => startInlineMajorEdit(degreeIdx, majorIdx, major.major_name)}
                                                                            style={{ background: 'transparent', border: 'none', color: '#1d4ed8', cursor: 'pointer' }}
                                                                        >
                                                                            Edit
                                                                        </button>
                                                                    )}
                                                                    <button
                                                                        onClick={() => handleRemoveMajorFromDegree(degreeIdx, majorIdx)}
                                                                        style={{ background: 'transparent', border: 'none', color: '#dc3545', cursor: 'pointer' }}
                                                                    >
                                                                        Remove
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div style={{ display: 'flex', gap: '10px', marginTop: '20px', flexWrap: 'wrap' }}>
                            <button
                                onClick={() => handleSaveDepartment()}
                                className="btn btn-primary"
                                style={{ flex: 1 }}
                            >
                                {isEditingDept ? 'Update Department' : 'Save Department'}
                            </button>
                            <button
                                onClick={closeDeptModal}
                                className="btn btn-outline"
                                style={{ flex: 1 }}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {resolutionModal && (
                <div className="modal-overlay" onClick={() => setResolutionModal(null)}>
                    <div className="modal" style={{ maxWidth: '680px' }} onClick={(e) => e.stopPropagation()}>
                        <h3 style={{ marginTop: 0 }}>Resolve Assigned Theses</h3>
                        <p className="subtitle" style={{ marginTop: 0 }}>
                            One or more items are assigned to thesis projects. Choose whether to delete those theses or move them to a specific department, degree, and major.
                        </p>
                        <div style={{ maxHeight: '420px', overflowY: 'auto', paddingRight: '8px' }}>
                            {resolutionModal.conflicts.map((conflict) => {
                                const key = conflictKey(conflict);
                                const selection = resolutionModal.selections[key] || {};
                                const targetDepartments = departments.filter(d => !(conflict.type === 'department' && String(d.dept_id) === String(conflict.id)));
                                const targetDegrees = getDegreesForResolution(selection).filter(d => !(conflict.type === 'degree' && String(d.degree_id) === String(conflict.id)));
                                const targetMajors = getMajorsForResolution(selection).filter(m => !(conflict.type === 'major' && String(m.major_id) === String(conflict.id)));
                                return (
                                    <div key={key} style={{ border: '1px solid #e5e7eb', borderRadius: '6px', padding: '12px', marginBottom: '12px', background: '#fafafa' }}>
                                        <strong style={{ display: 'block', marginBottom: '4px' }}>
                                            {conflict.type.charAt(0).toUpperCase() + conflict.type.slice(1)}: {conflict.name}
                                        </strong>
                                        <p style={{ margin: '0 0 10px', fontSize: '0.9rem', color: '#666' }}>
                                            Assigned to {conflict.thesisCount} thesis project(s).
                                        </p>
                                        <select
                                            className="input"
                                            value={selection.action || 'delete_theses'}
                                            onChange={(e) => updateResolutionSelection(conflict, { action: e.target.value })}
                                            style={{ marginBottom: '10px' }}
                                        >
                                            <option value="delete_theses">Delete all assigned theses</option>
                                            <option value="move_to">Move assigned theses to selected options</option>
                                            {conflict.type !== 'department' && <option value="keep_null">Keep theses without this {conflict.type}</option>}
                                        </select>

                                        {selection.action === 'move_to' && (
                                            <>
                                                <label style={{ fontSize: '0.8rem', fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>Department</label>
                                                <select
                                                    className="input"
                                                    value={selection.targetDeptId || ''}
                                                    onChange={(e) => updateResolutionSelection(conflict, { targetDeptId: e.target.value })}
                                                    style={{ marginBottom: '10px' }}
                                                >
                                                    <option value="">Select Department</option>
                                                    {targetDepartments.map(dept => (
                                                        <option key={dept.dept_id} value={dept.dept_id}>{dept.dept_name}</option>
                                                    ))}
                                                </select>

                                                {getDegreesForResolution(selection).length > 0 && (
                                                    <>
                                                        <label style={{ fontSize: '0.8rem', fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>Degree</label>
                                                        <select
                                                            className="input"
                                                            value={selection.targetDegreeId || ''}
                                                            onChange={(e) => updateResolutionSelection(conflict, { targetDegreeId: e.target.value })}
                                                            style={{ marginBottom: '10px' }}
                                                        >
                                                            <option value="">Select Degree</option>
                                                            {targetDegrees.map(degree => (
                                                                <option key={degree.degree_id} value={degree.degree_id}>{degree.degree_name}</option>
                                                            ))}
                                                        </select>
                                                    </>
                                                )}

                                                {getMajorsForResolution(selection).length > 0 && (
                                                    <>
                                                        <label style={{ fontSize: '0.8rem', fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>Major</label>
                                                        <select
                                                            className="input"
                                                            value={selection.targetMajorId || ''}
                                                            onChange={(e) => updateResolutionSelection(conflict, { targetMajorId: e.target.value })}
                                                        >
                                                            <option value="">Select Major</option>
                                                            {targetMajors.map(major => (
                                                                <option key={major.major_id} value={major.major_id}>{major.major_name}</option>
                                                            ))}
                                                        </select>
                                                    </>
                                                )}
                                            </>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                        <div className="modal-footer" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '14px' }}>
                            <button
                                className="btn btn-primary"
                                onClick={resolutionModal.mode === 'save-department' ? submitDepartmentSaveResolution : submitDeleteResolution}
                            >
                                Apply Resolution
                            </button>
                            <button className="btn btn-outline" onClick={() => setResolutionModal(null)}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}
            {renameModal && (
                <div className="modal-overlay" onClick={closeRenameModal}>
                    <div className="modal" style={{ maxWidth: '520px' }} onClick={(e) => e.stopPropagation()}>
                        <h3 style={{ marginTop: 0 }}>Edit {renameModal.type === 'degree' ? 'Degree' : 'Major'}</h3>
                        <p className="subtitle" style={{ marginTop: 0 }}>
                            Update the {renameModal.type} name. This will update all thesis projects assigned to it.
                        </p>
                        <input
                            type="text"
                            className="input"
                            value={renameModal.value}
                            onChange={(e) => setRenameModal(prev => ({ ...prev, value: e.target.value }))}
                            placeholder={`Enter ${renameModal.type} name`}
                            style={{ marginBottom: '16px' }}
                        />
                        <div className="modal-footer" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                            <button className="btn btn-primary" onClick={submitRenameModal}>Save</button>
                            <button className="btn btn-outline" onClick={closeRenameModal}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}
            <ConfirmModal state={confirmState} onClose={handleClose} />
        </div>
    );
};

export default AdminDashboard;
